/**
 * Multi-tenant account model.
 * Plans live at the account level (billing-plans-spec).
 *
 * Every operator belongs to one or more accounts. An account owns
 * projects + members + (transitively) repos and analytics views.
 *
 * DDB rows:
 *   PK = ACCOUNT#<id>  SK = META          — account record
 *   PK = ACCOUNT#<id>  SK = MEMBER#<email> — membership row
 *
 * Projects gain an `account_id` attribute so the existing project
 * routes can filter to repos the requester actually has access to.
 */

export type AccountRole = 'owner' | 'member';
export type MemberStatus = 'invited' | 'active';

// Re-export plan types used in the account billing context.
export type { ProjectPlan, PlanStatus } from './plans';

// ---------------------------------------------------------------------------
// DDB rows
// ---------------------------------------------------------------------------

export interface AccountMetaDynamoItem {
  PK: string;
  SK: 'META';
  id: string;
  name: string;
  owner_email: string;
  created_at: string;
  created_by: string;
  /** Canonical billing plan for this account (set by PUT /accounts/:id/plan). */
  plan?: string;
  plan_status?: 'active' | 'suspended';
}

export interface AccountMemberDynamoItem {
  PK: string;
  SK: string;        // MEMBER#<email>
  email: string;
  role: AccountRole;
  status: MemberStatus;
  invited_at?: string;
  invited_by?: string;
  joined_at?: string;
}

// ---------------------------------------------------------------------------
// API views
// ---------------------------------------------------------------------------

export interface AccountSummary {
  id: string;
  name: string;
  role: AccountRole;
  ownerEmail: string;
  createdAt: string;
  /** Billing plan for the account. Null when not yet set. */
  plan: import('./plans').ProjectPlan | null;
  planStatus: 'active' | 'suspended' | null;
  onboarding?: { step: string; completedAt: string | null } | null;
}

export interface AccountMember {
  email: string;
  role: AccountRole;
  status: MemberStatus;
  invitedAt: string | null;
  invitedBy: string | null;
  joinedAt: string | null;
}

/** Identity + every account the user is a member of. */
export interface CurrentUser {
  email: string;
  /** Cognito `sub` when available. */
  sub: string | null;
  accounts: AccountSummary[];
}

export interface MeResponse {
  data: CurrentUser;
}

export type ComplianceFramework = 'hipaa' | 'hitrust' | 'soc2' | 'fedramp' | 'pci';
export type BYOKProvider = 'bedrock' | 'openai' | 'vertex';
export type RepoCountBucket = '1' | '2-3' | '4-15' | '15+';
export type OrgCountBucket = '1' | '2+';
export type ProjectCountBucket = '1' | '2-3' | '4-10' | '10+';

export interface OnboardingAnswers {
  stacks: {
    backend?: string[];
    web?: string[];
    mobile?: string[];
    windows?: string[];
    database?: string[];
  };
  repoCount: RepoCountBucket;
  orgCount: OrgCountBucket;
  projectCount: ProjectCountBucket;
  needs: {
    testDeploys: boolean;
    browserSmoke: boolean;
    compliance: ComplianceFramework[];
    providers: BYOKProvider[];
    ssoScim: boolean;
  };
}

export interface OnboardRequest {
  /** Display name for the new account. */
  accountName: string;
  /** Billing plan chosen at Step 4 (server re-validates). */
  plan?: import('./plans').ProjectPlan;
  /** Questionnaire answers persisted for analytics. */
  answers?: OnboardingAnswers;
}

export interface OnboardResponse {
  data: {
    account: AccountSummary;
    /** How many legacy projects were claimed by this account. */
    claimedProjects: number;
    /**
     * True when the user already had an account before the call —
     * idempotent endpoint, doesn't throw on re-onboarding.
     */
    alreadyOnboarded: boolean;
  };
}

export interface OnboardingStateResponse {
  data: { step: string; completedAt: string | null };
}

export interface PatchOnboardingRequest {
  step: string;
  completedAt?: string | null;
}

export interface ListMembersResponse {
  data: { members: AccountMember[] };
}

export interface InviteMemberRequest {
  email: string;
  role?: AccountRole;
}

export interface InviteMemberResponse {
  data: AccountMember;
}

// ---------------------------------------------------------------------------
/**
 * Create an additional workspace for the authenticated user. Unlike
 * `OnboardRequest` (idempotent first-time bootstrap), this endpoint
 * always tries to create a fresh ACCOUNT# row — the caller becomes the
 * owner. Supply `id` to control the slug, or let the server derive it
 * from `name`.
 */
export interface CreateAccountRequest {
  name: string;
  id?: string;
}

export interface CreateAccountResponse {
  data: AccountSummary;
}

// ---------------------------------------------------------------------------
// Error envelope shared with the rest of the API.
// ---------------------------------------------------------------------------

export interface AccountErrorResponse {
  error: {
    code:
      | 'INVALID_REQUEST'
      | 'UNAUTHORIZED'
      | 'FORBIDDEN'
      | 'NOT_FOUND'
      | 'MEMBER_EXISTS'
      | 'INTERNAL_ERROR';
    message: string;
  };
}

// ---------------------------------------------------------------------------
// Plan switching
// ---------------------------------------------------------------------------

import type { PlanViolation } from './projects';

export interface SwitchAccountPlanRequest {
  newPlan: import('./plans').ProjectPlan;
}

export interface SwitchAccountPlanResponse {
  data: AccountSummary;
}

/** Returned as 422 when downgrading would exceed one or more plan limits. */
export interface AccountPlanConflictBody {
  error: {
    code: 'PLAN_VIOLATION';
    message: string;
    /** Per-project violations; each carries projectId + projectName. */
    violations: PlanViolation[];
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Email validation that mirrors the Cognito signup form (loose RFC 5321). */
export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/** Build a stable, slug-safe account id from an email + timestamp. */
export function suggestAccountId(email: string): string {
  const base = email
    .split('@')[1]
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'account';
  // Append a 4-char timestamp-derived suffix so concurrent signups
  // never collide on the same domain.
  const suffix = Math.floor(Date.now() / 1000).toString(36).slice(-4);
  return `${base}-${suffix}`;
}

/** Account-id slug used in URLs and DDB keys: lowercase, dashes, 1..32. */
export const ACCOUNT_ID_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

/**
 * Derive an account id from a workspace name. Lowercases, replaces
 * non-alphanumerics with dashes, trims, caps at 32 chars. Returns
 * null when nothing usable remains.
 */
export function accountIdFromName(name: string): string | null {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  if (!slug || !ACCOUNT_ID_PATTERN.test(slug)) return null;
  return slug;
}
